import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

export interface DataTableColumn<Row> {
  key: keyof Row & string;
  label: string;
  render?: (value: Row[keyof Row], row: Row) => React.ReactNode;
  sortable?: boolean;
  className?: string;
}

export interface DataTableSort<Row> {
  key: keyof Row & string;
  dir: "asc" | "desc";
}

export interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  data: Row[];
  defaultSort?: DataTableSort<Row>;
  searchKeys?: (keyof Row & string)[];
  className?: string;
  emptyMessage?: string;
  /** Rows shown before a "Show all" toggle appears. Ignored while a search
   * is active — a filtered result set is already narrowed down, so capping
   * it further would hide the very rows the search was looking for. */
  maxVisible?: number;
}

// Once more than this many rows are actually rendered (whether because
// "Show all" was clicked or a search still matches a lot of rows), the table
// scrolls inside its own bounded window instead of pushing the rest of the
// page down. Keeps a wide/large table from swallowing the tab's period
// picker and nav off the top of the screen.
const SCROLL_THRESHOLD = 15;
// ~42px per row (header included) — 640px shows roughly 14 rows before the
// internal scrollbar engages. Tuned up from an earlier 420px (~9 rows),
// which felt too short even for the default capped view.
const SCROLL_MAX_HEIGHT = "max-h-[640px]";
const SEARCH_DEBOUNCE_MS = 200;

export function DataTable<Row extends Record<string, unknown>>({
  columns,
  data,
  defaultSort,
  searchKeys,
  className = "",
  emptyMessage = "No data",
  maxVisible = 25,
}: DataTableProps<Row>): React.JSX.Element {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<DataTableSort<Row> | null>(defaultSort ?? null);
  const [showAll, setShowAll] = useState(false);

  const hasSearch = (searchKeys?.length ?? 0) > 0;

  // The input updates immediately so typing feels instant; the value that
  // actually drives filtering lags slightly behind so a fast typist over a
  // large table isn't re-filtering/re-sorting on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [searchInput]);

  const filtered = useMemo<Row[]>(() => {
    if (!search || !hasSearch) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      (searchKeys ?? []).some((k) => String(row[k] ?? "").toLowerCase().includes(q)),
    );
  }, [data, search, searchKeys, hasSearch]);

  const sorted = useMemo<Row[]>(() => {
    if (!sort) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const an = typeof av === "number" ? av : parseFloat(String(av));
      const bn = typeof bv === "number" ? bv : parseFloat(String(bv));
      const numeric = !isNaN(an) && !isNaN(bn);
      const cmp = numeric
        ? an - bn
        : String(av ?? "").localeCompare(String(bv ?? ""));
      return sort.dir === "desc" ? -cmp : cmp;
    });
  }, [filtered, sort]);

  // Searching already narrows the set down, so it bypasses the cap —
  // otherwise a search could match more rows than maxVisible and hide the
  // very thing being searched for.
  const isCapped = !search && !showAll && sorted.length > maxVisible;
  const visible = isCapped ? sorted.slice(0, maxVisible) : sorted;
  const shouldScroll = visible.length > SCROLL_THRESHOLD;

  const toggleSort = (key: keyof Row & string): void => {
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "desc" },
    );
  };

  const headerRow = (
    <tr className="border-b-2 border-border">
      {columns.map((col) => {
        const sortable = col.sortable !== false;
        const active = sort !== null && sort.key === col.key;
        const ariaSort: React.AriaAttributes["aria-sort"] = active
          ? sort.dir === "asc"
            ? "ascending"
            : "descending"
          : sortable
            ? "none"
            : undefined;
        return (
          <th
            key={col.key}
            aria-sort={ariaSort}
            className={`text-left py-2.5 px-3 section-label font-body font-normal
                        ${col.className ?? ""}`}
          >
            {sortable ? (
              <button
                type="button"
                onClick={() => {
                  toggleSort(col.key);
                }}
                className="flex items-center gap-1 select-none cursor-pointer hover:text-foreground w-full text-left"
              >
                {col.label}
                {active && (
                  <span className="text-phoenix-orange text-xs">
                    {sort.dir === "desc" ? "↓" : "↑"}
                  </span>
                )}
              </button>
            ) : (
              col.label
            )}
          </th>
        );
      })}
    </tr>
  );

  return (
    <div className={className}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        {hasSearch ? (
          <div className="relative">
            <input
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              placeholder="Filter..."
              aria-label="Filter rows"
              className="border border-border rounded-lg pl-3 pr-8 py-1.5 text-sm font-body
                         focus:outline-none focus:ring-2 focus:ring-ring focus:border-phoenix-orange
                         transition-colors w-64"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                }}
                aria-label="Clear filter"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ) : (
          <span />
        )}

        {sorted.length > 0 && (
          <p className="text-[11px] text-muted-foreground font-body whitespace-nowrap">
            {search
              ? `${String(sorted.length)} of ${String(data.length)} ${data.length === 1 ? "row" : "rows"} (filtered)`
              : isCapped || showAll
                ? `${String(visible.length)} of ${String(sorted.length)} rows`
                : `${String(sorted.length)} ${sorted.length === 1 ? "row" : "rows"}`}
            {isCapped && (
              <button
                type="button"
                onClick={() => {
                  setShowAll(true);
                }}
                className="ml-2 font-medium text-phoenix-orange hover:underline"
              >
                Show all {sorted.length}
              </button>
            )}
            {showAll && sorted.length > maxVisible && (
              <button
                type="button"
                onClick={() => {
                  setShowAll(false);
                }}
                className="ml-2 font-medium text-phoenix-orange hover:underline"
              >
                Show first {maxVisible}
              </button>
            )}
          </p>
        )}
      </div>

      <div className={shouldScroll ? `overflow-auto ${SCROLL_MAX_HEIGHT}` : "overflow-x-auto"}>
        <table className="w-full text-sm font-body">
          <thead className={shouldScroll ? "sticky top-0 bg-card" : undefined}>{headerRow}</thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-8 text-muted-foreground font-body"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              visible.map((row, i) => (
                <tr
                  key={i}
                  className="border-b border-border/50 hover:bg-muted/40 transition-colors"
                >
                  {columns.map((col) => {
                    const value = row[col.key];
                    const cell = col.render
                      ? col.render(value, row)
                      : value == null || value === ""
                        ? "—"
                        : String(value);
                    return (
                      <td key={col.key} className={`py-2.5 px-3 ${col.className ?? ""}`}>
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
